import 'react-toastify/dist/ReactToastify.css';
import React, { useEffect } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import { RouterProvider } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from 'components/ThemeProvider';
import { AppRoutesElements } from './routes';
import { SplashScreenPage } from 'pages/Common/SplashScreenPage';
import { useGetMyPersonalization, useGetTenantConfigData } from 'services/v1/Tenant/ConfigurationService';
import { useDispatch } from 'react-redux';
import { setConfig, setSetupStatus } from 'store/reducer/settingReducer';
import { TENANT_CONFIGURATION_KEY } from 'constant/TenantConfigConstant';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { DASHBOARD_TYPE_CONSTANT } from 'constant/DashboardTypeConstant';
import { useIdleTimer } from 'react-idle-timer';
import { setSessionStatus, setRememberMe } from 'store/reducer/authReducer';
import { SESSION_STATUS_CONSTANT } from 'constant/SessionStatusConstant';
import { dayjs } from 'utils/DateUtils';
export function MainComponent() {
  const dispatch = useDispatch();
  const { selectedTenant, accessToken, expiredDate, rememberMe } = useSelector((state: RootState) => state.auth);
  const splashScreenRef = React.useRef<Player | null>(null);

  const onIdle = () => {
    if (accessToken) {
      dispatch(setSessionStatus(SESSION_STATUS_CONSTANT.IDLE_EXPIRED));
    }
  };

  const { isIdle } = useIdleTimer({
    onIdle,
    timeout: 1000 * 60 * 15, // 15 minutes
  });

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (accessToken && expiredDate) {
      interval = setInterval(() => {
        const remainingTime = dayjs(expiredDate).diff(dayjs(), 'milliseconds');
        // if remaining time is between 0 and 1 minute (60 * 1000 ms), and user is not idle
        if (remainingTime > 0 && remainingTime <= 60000) {
          if (!isIdle()) {
            dispatch(
              setRememberMe({
                rememberMe: rememberMe,
                expiredDate: dayjs(expiredDate).add(10, 'minutes').toISOString(),
              }),
            );
          }
        }
      }, 10000); // Check every 10s
    }
    return () => clearInterval(interval);
  }, [accessToken, expiredDate, isIdle, dispatch, rememberMe]);

  const isCustomerTenant =
    selectedTenant.tenant.dashboardType === DASHBOARD_TYPE_CONSTANT.DASHBOARD_TENANT ||
    selectedTenant.tenant.dashboardType === DASHBOARD_TYPE_CONSTANT.DASHBOARD_MASTER;
  const { data: tenantConfigData } = useGetTenantConfigData({
    refetchInterval: false,
    enabled: isCustomerTenant,
  });

  useGetMyPersonalization({
    enabled: isCustomerTenant,
  });
  const [isSplashScreen, setIsSplashScreen] = React.useState(selectedTenant.roleType ? true : false);

  useEffect(() => {
    if (tenantConfigData?.data) {
      const setupStatus =
        tenantConfigData.data.find((item) => item.id === TENANT_CONFIGURATION_KEY.INITIAL_SETUP_STATUS)?.value || null;
      dispatch(setConfig(tenantConfigData.data));
      setupStatus && dispatch(setSetupStatus(setupStatus));
      splashScreenRef.current?.play();
      setTimeout(() => {
        setIsSplashScreen(false);
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantConfigData]);

  if (isSplashScreen && isCustomerTenant) return <SplashScreenPage innerRef={splashScreenRef} />;

  return (
    <>
      <ToastContainer />
      <ThemeProvider>
        <RouterProvider router={AppRoutesElements} />
      </ThemeProvider>
    </>
  );
}
